import React, { useState, useEffect } from "react";
import {
  Search,
  X,
  Loader2,
  RefreshCw,
  Trash2,
  UserX,
  TriangleAlert,
} from "lucide-react";
import useSettings from "../../hooks/useSettings";
import { Icons } from "../../components/icons";
import DatePicker from "react-datepicker";

const STATUS_STYLE = {
  active: "bg-[#CFFFDE] text-[#003C18]",
  inactive: "bg-[#F5F5F5] text-[#7F7F7F]",
  pending: "bg-[#FEF2D4] text-[#7C4A10]",
};

function statusLabel(s) {
  return s?.toUpperCase() ?? "—";
}

function fmt(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function fmtTime(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Date-only values must stay in local time. Parsing or serializing with UTC
// (`new Date("YYYY-MM-DD")` / `toISOString`) can shift the chosen day.
function parseLocalDate(dateString) {
  if (!dateString) return null;
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function formatLocalDate(date) {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function getProfileImageUrl(profilePicture) {
  if (!profilePicture) return "";
  if (typeof profilePicture === "string") return profilePicture;
  return profilePicture.url || "";
}

// ─── Pagination Bar ───────────────────────────────────────────────────────────

function Pagination({ current, total, onChange }) {
  if (total <= 1) return null;
  return (
    <div className="flex gap-3 items-center">
      <button
        className="w-10 h-10 border border-[#ECEFF5] rounded-lg text-[0px] bg-[#F5F5F5] flex items-center justify-center disabled:opacity-60 before:block before:w-3 before:h-3 before:border-l-2 before:border-b-2 before:border-gray-700 before:rotate-45"
        disabled={current === 1}
        onClick={() => onChange(current - 1)}
      >
        ‹
      </button>
      {Array.from({ length: total }, (_, i) => i + 1).map((p) => (
        <button
          key={p}
          className={`w-8 h-8 rounded-lg text-xs ${p === current ? "bg-[#4C5CE7] text-white" : "text-black"}`}
          onClick={() => onChange(p)}
        >
          {p}
        </button>
      ))}
      <button
        className="w-10 h-10 border border-[#ECEFF5] rounded-lg text-[0px] bg-[#F5F5F5] flex items-center justify-center disabled:opacity-60 before:block before:w-3 before:h-3 before:border-r-2 before:border-t-2 before:border-gray-700 before:rotate-45"
        disabled={current === total}
        onClick={() => onChange(current + 1)}
      >
        ›
      </button>
    </div>
  );
}

// ─── Document Row ─────────────────────────────────────────────────────────────
function DocRow({ label, url }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  const handleDownload = async () => {
    if (!url) {
      setDownloadError("No document found.");
      return;
    }
    setDownloadError("");
    setIsDownloading(true);
    try {
      const response = await fetch(url);
      if (!response.ok)
        throw new Error(`Request failed with status ${response.status}`);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = url.split("/").pop()?.split("?")[0] || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setDownloadError("Couldn't download this file. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex items-center gap-2 mb-2">
      <div
        className="flex items-center justify-between border rounded-lg px-4 bg-white"
        style={{ width: "367px", height: "39px" }}
      >
        <span className="text-[10px] font-normal text-gray-400">{label}</span>

        {/* 👇 REPLACED the <a> tag with this <button> */}

        {url && (
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className="hover:opacity-70 transition-opacity disabled:opacity-50"
          >
            {isDownloading ? (
              <Loader2 size={16} className="animate-spin text-gray-400" />
            ) : (
              <Icons.Downloads />
            )}
          </button>
        )}
        {downloadError && (
          <p className="text-[10px] text-red-500 mt-1">{downloadError}</p>
        )}
      </div>

      {/* VIEW button - outside the box */}
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="bg-[#4253C7] text-white text-xs px-4 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ height: "39px" }}
        >
          VIEW
        </a>
      ) : (
        <div style={{ height: "39px" }} />
      )}
    </div>
  );
}
function ConfirmToggleModal({ action, onCancel, onConfirm, isLoading, error }) {
  if (!action) return null;
  const { user, type } = action;
  const isEnable = type === "enable";
  const accentColor = isEnable ? "#4253C7" : "#EF4444";
  const actionColor = isEnable ? "#4253C7" : "#EF4444";

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-50"
        onClick={onCancel}
      />
      <div
        className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl z-50 w-[635px] max-w-[calc(100vw-32px)] ${isEnable ? "h-[430px]" : "h-[524px]"} max-h-[90vh] overflow-y-auto p-6 sm:p-10 shadow-2xl flex flex-col`}
      >
        {/* Header: icon + title + close */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div
              className={`w-[42px] h-[42px] rounded-full ${isEnable ? "bg-[#CDDDFA]" : "bg-[#FFF0F0]"} flex items-center justify-center flex-shrink-0`}
            >
              {isEnable ? (
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={accentColor}
                  strokeWidth="1.4"
                >
                  <circle cx="12" cy="8" r="3.2" />
                  <path d="M5 20c0-3.3 3.1-5.8 7-5.8s7 2.5 7 5.8" />
                </svg>
              ) : (
                <UserX size={24} color={accentColor} strokeWidth={2.2} />
              )}
            </div>
            <h2 className="text-[24px] leading-[32px] font-semibold text-black">
              {isEnable ? "Enable Account" : "Disable Account"}
            </h2>
          </div>
          <button onClick={onCancel} className="mt-1">
            <X size={33} className="text-black opacity-50" />
          </button>
        </div>

        {/* Description */}
        <p className="text-[14px] leading-6 text-black mt-[14px] w-full max-w-[555px]">
          {isEnable
            ? "Are you sure you want to enable this account and restore the user's access to QwickHub?"
            : "Are you sure you want to disable this account ? The user will no longer be able to sign in."}
        </p>

        {/* User info box */}
        <div className="border border-[rgba(0,0,0,0.19)] rounded-lg px-4 py-[17px] mt-10 flex items-center justify-between w-full max-w-[555px] min-h-[86px]">
          <div className="flex items-center gap-3 min-w-0">
            {getProfileImageUrl(user.profilePicture) ? (
              <img
                src={getProfileImageUrl(user.profilePicture)}
                alt={user.name}
                className="w-[52px] h-[52px] rounded-full object-cover"
              />
            ) : (
              <div className="w-[52px] h-[52px] rounded-full bg-gray-200 flex items-center justify-center text-lg font-semibold text-gray-600">
                {user.name?.[0] ?? "?"}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[14px] leading-6 text-black capitalize truncate">
                {user.name}
              </p>
              <p className="text-[12px] leading-6 text-[#7F7F7F] lowercase mt-1 truncate">
                {user.email}
              </p>
            </div>
          </div>
          <div className="text-left min-w-[59px] ml-6">
            <p className="text-[14px] leading-6 text-black capitalize">Role</p>
            <p className="text-[12px] leading-6 text-[#7F7F7F] capitalize mt-1">
              {user.role}
            </p>
          </div>
        </div>

        {!isEnable && (
          <div className="mt-7 rounded-lg px-5 py-[15px] flex items-center gap-3 w-full max-w-[555px] min-h-[66px] shadow-[0_0_4px_rgba(0,0,0,0.05)] bg-[#FFF0F0]">
            <TriangleAlert
              size={24}
              fill={actionColor}
              color={actionColor}
              className="flex-shrink-0"
            />
            <p
              className="text-xs leading-[18px] capitalize"
              style={{ color: accentColor }}
            >
              This user will lose access to QwickHub, including projects, tasks,
              and notifications. Access will remain disabled until the account
              is re-enabled.
            </p>
          </div>
        )}

        {error && <p className="text-sm text-red-500 mt-3">{error}</p>}

        {/* Buttons */}
        <div className="flex justify-end gap-3 mt-auto pt-10">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="border w-[163px] h-[40px] rounded-lg text-[12px] flex items-center justify-center disabled:opacity-60"
            style={{ borderColor: accentColor, color: accentColor }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="text-white w-[163px] h-[40px] rounded-lg text-[12px] flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ backgroundColor: actionColor }}
          >
            {isLoading && <Loader2 size={14} className="animate-spin" />}
            {isEnable ? "Enable" : "Disable"}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Side Panel ───────────────────────────────────────────────────────────────
// Used for both destructive actions. `permanent` switches the copy between
// archiving (reversible, from the Resources list) and permanent deletion
// (irreversible, from the Archived tab) — the layout is identical.
function DeleteUserModal({
  user,
  onCancel,
  onConfirm,
  isLoading,
  error,
  permanent = true,
}) {
  if (!user) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-50"
        onClick={onCancel}
      />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl z-50 w-[635px] max-w-[calc(100vw-32px)] h-[500px] max-h-[90vh] overflow-y-auto p-6 sm:p-10 shadow-2xl flex flex-col">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-[42px] h-[42px] rounded-full bg-[#FFF0F0] flex items-center justify-center flex-shrink-0">
              <Trash2 size={24} color="#EF4444" strokeWidth={2.2} />
            </div>
            <h2 className="text-[24px] leading-[32px] font-semibold text-black">
              {permanent ? "Delete User" : "Archive User"}
            </h2>
          </div>
          <button onClick={onCancel} className="mt-1">
            <X size={33} className="text-black opacity-50" />
          </button>
        </div>

        <p className="text-[14px] leading-6 text-black mt-[14px] w-full max-w-[574px]">
          {permanent
            ? "Are you sure you want to permanently delete this user from QwickHub?"
            : "Are you sure you want to remove this user from your resources?"}
        </p>

        <div className="border border-[rgba(0,0,0,0.19)] rounded-lg px-4 py-[17px] mt-10 flex items-center justify-between w-full max-w-[555px] min-h-[86px]">
          <div className="flex items-center gap-3 min-w-0">
            {user.profilePicture ? (
              <img
                src={user.profilePicture}
                alt={user.name}
                className="w-[52px] h-[52px] rounded-full object-cover"
              />
            ) : (
              <div className="w-[52px] h-[52px] rounded-full bg-gray-200 flex items-center justify-center text-lg font-semibold text-gray-600">
                {user.name?.[0] ?? "?"}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[14px] leading-6 text-black capitalize truncate">
                {user.name}
              </p>
              <p className="text-[12px] leading-[21px] text-[#7F7F7F] lowercase mt-1 truncate">
                {user.email}
              </p>
            </div>
          </div>
          <div className="text-left min-w-[59px] ml-6">
            <p className="text-[14px] leading-6 text-black capitalize">Role</p>
            <p className="text-[12px] leading-[21px] text-[#7F7F7F] capitalize mt-1">
              {user.role}
            </p>
          </div>
        </div>

        <div
          className={`mt-7 rounded-lg px-5 py-[15px] flex items-center gap-3 w-full max-w-[555px] min-h-[66px] shadow-[0_0_4px_rgba(0,0,0,0.05)] ${
            permanent ? "bg-[#FFF0F0]" : "bg-[#FFF8E5]"
          }`}
        >
          <TriangleAlert
            size={24}
            fill={permanent ? "#EF4444" : "#D4A72C"}
            color={permanent ? "#EF4444" : "#D4A72C"}
            className="flex-shrink-0"
          />
          <p
            className={`text-xs leading-[18px] capitalize ${
              permanent ? "text-[#FF0000]" : "text-[#7D4E00]"
            }`}
          >
            {permanent ? (
              <>
                This user will be permanently deleted from QwickHub.
                <br />
                This action cannot be undone.
              </>
            ) : (
              <>
                This user will be moved to Archived and will lose access.
                <br />
                You can restore them from the Archived tab.
              </>
            )}
          </p>
        </div>

        {error && <p className="text-sm text-red-500 mt-3">{error}</p>}

        <div className="flex justify-end gap-3 mt-10 pt-10">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="border border-[#FF0000] text-[#FF0000] w-[163px] h-[40px] rounded-lg text-[12px] flex items-center justify-center disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="bg-[#EF4444] text-white w-[163px] h-[40px] rounded-lg text-[12px] flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {isLoading && <Loader2 size={14} className="animate-spin" />}
            {permanent ? "Delete" : "Archive"}
          </button>
        </div>
      </div>
    </>
  );
}

// ✅ CORRECT
function SidePanel({ userId, onClose }) {
  const { selectedUser, isLoadingUser, loadUser, clearUser } = useSettings();

  useEffect(() => {
    if (!userId) return;
    loadUser(userId);
    return () => clearUser();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-40"
        onClick={onClose}
      />
      <div className="fixed top-0 right-0 h-[1024px] max-h-screen w-full max-w-[484px] bg-white z-50 overflow-y-auto shadow-2xl [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <div className="p-6 pb-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">Resource Details</h2>
            <button onClick={onClose}>
              <X size={20} className="text-gray-400" />
            </button>
          </div>
          <hr className="mb-6" />

          {isLoadingUser ? (
            <div className="flex items-center justify-center h-60">
              <Loader2 className="animate-spin text-[#4253C7]" size={32} />
            </div>
          ) : !selectedUser ? (
            <p className="text-center text-gray-400 mt-20">
              Could not load details.
            </p>
          ) : (
            <>
              {/* Avatar + name */}
              <div className="flex flex-col items-center text-center mb-6">
                {getProfileImageUrl(selectedUser.profilePicture) ? (
                  <img
                    src={getProfileImageUrl(selectedUser.profilePicture)}
                    alt={selectedUser.name}
                    className="w-20 h-20 rounded-full object-cover mb-3"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-2xl font-bold text-gray-600 mb-3">
                    {selectedUser.name?.[0] ?? "?"}
                  </div>
                )}
                <h3 className="text-[24px] font-normal">{selectedUser.name}</h3>
                <p className="text-gray-400 text-sm">{selectedUser.email}</p>

                {selectedUser.specialization?.length > 0 && (
                  <div className="flex flex-wrap gap-2 justify-center mt-3 max-w-[328px]">
                    {selectedUser.specialization.map((s, i) => (
                      <span
                        key={i}
                        className="bg-blue-100 text-blue-600 text-xs px-3 py-1 rounded-full w-[104px] text-center truncate"
                      >
                        {s.toUpperCase()}
                      </span>
                    ))}
                  </div>
                )}

                {selectedUser.employeeId && (
                  <p className="text-sm mt-3 text-gray-600">
                    • {selectedUser.employeeId}
                  </p>
                )}
              </div>

              {/* Personal Info */}
              <div className="mb-4 px-1">
                <h4 className="text-[16px] font-normal text-gray-700 mb-4">
                  PERSONAL INFO
                </h4>
                <hr className="mb-4" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-5">
                  {[
                    [
                      "DOB",
                      selectedUser.dateOfBirth
                        ? fmt(selectedUser.dateOfBirth)
                        : "—",
                    ],
                    ["Personal Email", selectedUser.personalEmail || "—"],
                    ["Phone No", selectedUser.phone || "—"],
                    ["Alternate Phone", selectedUser.alternatePhone || "—"],
                    ["Blood Group", selectedUser.bloodGroup || "—"],
                    [
                      "Joined",
                      selectedUser.dateOfJoining
                        ? fmt(selectedUser.dateOfJoining)
                        : "—",
                    ],
                  ].map(([label, val]) => (
                    <div key={label}>
                      <p className="text-[14px] text-gray-400 uppercase tracking-wider">
                        {label}
                      </p>
                      <p className="text-[12px] font-normal mt-1 break-all">
                        {val}
                      </p>
                    </div>
                  ))}
                  <div className="col-span-2">
                    <p className="text-[14px] text-gray-400 uppercase tracking-wider">
                      Address
                    </p>
                    <p className="text-[12px] font-normal mt-1">
                      {selectedUser.address || "—"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="mt-8 mb-6 px-1">
                <p className="text-[14px] text-gray-400 uppercase tracking-wider mb-3 ">
                  DOCUMENT
                </p>{" "}
  {[
  { key: "aadhaar", label: "AADHAAR CARD" },
  { key: "pan", label: "PAN CARD" },
  { key: "marksCard", label: "MARKS CARD" },
  { key: "experienceLetter", label: "EXPERIENCE LETTER" },
]
  .map(({ key, label }) => {
    const doc = selectedUser.documents?.[key];
    const url = typeof doc === "string" ? doc : doc?.url;
    return { key, label, url };
  })
  .filter((doc) => doc.url) // 👈 only keep documents that were actually uploaded
  .map(({ key, label, url }) => (
    <DocRow key={key} label={label} url={url} />
  ))}
              </div>

              <button
                onClick={onClose}
                className="w-full mt-8 mb-4 border border-[#4253C7] text-[#4253C7] py-3 rounded-xl text-sm font-medium"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Designations ─────────────────────────────────────────────────────────────

const DESIGNATIONS = [
  { label: "Trainee Software Engineer - TSE", value: "TSE" },
  { label: "Associate Software Engineer - ASE", value: "ASE" },
  { label: "Software Engineer - SDE1", value: "SDE1" },
  { label: "Senior Software Engineer - SDE2", value: "SDE2" },
  { label: "Lead Software Engineer - SDE3", value: "SDE3" },
  { label: "Technical Lead - TL", value: "TL" },
  { label: "Team Lead - TML", value: "TML" },
  { label: "Technical Architect - TA", value: "TA" },
  { label: "Project Lead - PL", value: "PL" },
  { label: "Associate Project Manager - APM", value: "APM" },
  { label: "Project Manager - PM", value: "PM" },
  { label: "Business Analyst - BA", value: "BA" },
  { label: "Senior Business Analyst - SBA", value: "SBA" },
  { label: "Test Engineer - TE", value: "TE" },
  { label: "Senior Test Engineer - STE", value: "STE" },
  { label: "Testing Team Lead - TTL", value: "TTL" },
  { label: "UX Design Engineer - UXDE", value: "UXDE" },
  { label: "Senior Design Engineer - SDEG", value: "SDEG" },
  { label: "Business Development Manager - BDM", value: "BDM" },
  { label: "Marketing & Sales Executive - MSE", value: "MSE" },
  { label: "Human Resources Executive - HR", value: "HR" },
  { label: "Senior HR Executive - SHR", value: "SHR" },
  { label: "HR Manager - HRM", value: "HRM" },
  { label: "Assistant HR Manager - AHRM", value: "AHRM" },
];

const DEFAULT_INVITE_FORM = {
  email: "",
  role: "member",
  specialization: ["Frontend"],
  dateOfJoining: "",
  designation: "",
  description: "",
};
// ─── Main Component ───────────────────────────────────────────────────────────

const Resources = () => {
  const [activeView, setActiveView] = useState("resources");
  const [panelUserId, setPanelUserId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const TABLE_MIN_HEIGHT = 360;
  const [inviteForm, setInviteForm] = useState(DEFAULT_INVITE_FORM);

  const {
    resources,
    resPagination,
    archived,
    archPagination,
    isLoadingResources,
    isLoadingArchived,
    isInviting,
    isToggling,
    toggleError,
    loadUsers,
    loadArchivedUsers,
    sendInvite,
    toggleUserStatus,
    restoreArchivedUser,
    archiveResource,
    permanentlyDeleteUser,
    clearInvite,
  } = useSettings();

  // Keep these local — they're UI state only:
  const [resSearch, setResSearch] = useState("");
  const [resPage, setResPage] = useState(1);
  const [archSearch, setArchSearch] = useState("");
  const [archPage, setArchPage] = useState(1);
  const [inviteFormError, setInviteFormError] = useState("");

  // ── Invite modal ──
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isInviteSentOpen, setIsInviteSentOpen] = useState(false);
  const [isDesignationOpen, setIsDesignationOpen] = useState(false);
  const [isSpecializationOpen, setIsSpecializationOpen] = useState(false);

  // ── Fetch resources ───────────────────────────────────────────────────────
  // Load users on first render
  useEffect(() => {
    loadUsers(1, "");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load archived when switching to archived view
  useEffect(() => {
    if (activeView === "archived") {
      loadArchivedUsers(1, "");
    }
  }, [activeView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search debounce for resources
  useEffect(() => {
    const t = setTimeout(() => {
      setResPage(1);
      loadUsers(1, resSearch);
    }, 350);

    return () => clearTimeout(t);
  }, [resSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search debounce for archived
  useEffect(() => {
    if (activeView !== "archived") return;
    const t = setTimeout(() => {
      setArchPage(1);
      loadArchivedUsers(1, archSearch);
    }, 350);
    return () => clearTimeout(t);
  }, [archSearch, activeView]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pagination handlers ───────────────────────────────────────────────────
  const handleResPage = (p) => {
    setResPage(p);
    loadUsers(p, resSearch);
  };
  const handleArchPage = (p) => {
    setArchPage(p);
    loadArchivedUsers(p, archSearch);
  };

  // ── Toggle (inline, no panel) ─────────────────────────────────────────────
  const handleToggle = (user) => {
    setConfirmAction({
      user,
      type: user.status === "active" ? "disable" : "enable",
    });
  };

  const handleConfirmToggle = async () => {
    if (!confirmAction) return;
    const result = await toggleUserStatus(confirmAction.user._id);
    if (result.meta.requestStatus === "fulfilled") {
      setConfirmAction(null); // close the popup
    }
  };

  // ── Restore ───────────────────────────────────────────────────────────────
  const handleRestore = async (userId) => {
    const result = await restoreArchivedUser(userId);
    if (result.meta.requestStatus === "fulfilled") {
      loadUsers(resPage, resSearch);
      loadArchivedUsers(archPage, archSearch);
    } else {
      alert(result.payload || "Failed to restore.");
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = (user) => {
    setDeleteError("");
    setDeleteUser(user);
  };
  // "Delete" means two different things depending on the tab:
  //   Resources → archive (reversible; the user moves to the Archived tab)
  //   Archived  → permanent deletion (irreversible)
  const isPermanentDelete = activeView === "archived";

  const handleConfirmDelete = async () => {
    if (!deleteUser) return;
    setIsDeletingUser(true);
    setDeleteError("");
    const result = isPermanentDelete
      ? await permanentlyDeleteUser(deleteUser._id)
      : await archiveResource(deleteUser._id);
    setIsDeletingUser(false);
    if (result.meta.requestStatus === "fulfilled") {
      setDeleteUser(null);
      loadUsers(resPage, resSearch);
      // Refresh Archived either way: a permanent delete removes a row from it,
      // and an archive adds one, so it must not be shown stale on the way back.
      loadArchivedUsers(archPage, archSearch);
    } else {
      setDeleteError(
        result.payload ||
          (isPermanentDelete ? "Failed to delete." : "Failed to archive."),
      );
    }
  };
  // ── Invite submit ─────────────────────────────────────────────────────────
  const handleInviteSubmit = async () => {
    setInviteFormError("");
    if (!inviteForm.email) {
      setInviteFormError("Email is required.");
      return;
    }
    if (!inviteForm.dateOfJoining) {
      setInviteFormError("Start date is required.");
      return;
    }

    const result = await sendInvite(inviteForm);
    if (result.meta.requestStatus === "fulfilled") {
      setIsInviteOpen(false);
      setIsInviteSentOpen(true);
      clearInvite();
      loadUsers(resPage, resSearch);
    } else {
      setInviteFormError(result.payload || "Failed to send invite.");
    }
  };

  const handleInviteChange = (e) => {
    const { name, value } = e.target;
    setInviteForm((p) => ({
      ...p,
      [name]: value,
      ...(name === "role" && value === "viewer"
        ? { specialization: [] }
        : name === "role" ? { description: "" } : {}),
    }));
    if (name === "role" && value === "viewer") {
      setIsSpecializationOpen(false);
      setIsDesignationOpen(false);
    }
    setInviteFormError("");
  };
  const SPECIALIZATIONS = [
    "Backend",
    "Frontend",
    "FullStack",
    "Design",
    "DevOps",
    "QA/Testing",
    "AI/ML",
    "Data",
    "Security",
    "Cloud",
    "MobileDev",
    "BusinessAnalyst",
    "TeamLead",
    "ProjectManager",
    "Intern",
    "Network",
    "Marketing",
    "Sales",
    "Finance",
  ];
  const selectedSpecializations = Array.isArray(inviteForm.specialization)
    ? inviteForm.specialization
    : inviteForm.specialization
      ? [inviteForm.specialization]
      : [];
  const specializationLabel = selectedSpecializations.length
    ? selectedSpecializations.join(", ")
    : "Select specialization";
  const handleSpecializationToggle = (specialization) => {
    if (isViewer) return;
    setInviteForm((prev) => {
      const current = Array.isArray(prev.specialization)
        ? prev.specialization
        : prev.specialization
          ? [prev.specialization]
          : [];
      const next = current.includes(specialization)
        ? current.filter((item) => item !== specialization)
        : [...current, specialization];

      return { ...prev, specialization: next };
    });
    setInviteFormError("");
  };

  const isViewer = inviteForm.role === "viewer";
  const viewerDescription = inviteForm.description || "";

  return (
    <>
      <div className="bg-white rounded-xl p-5 overflow-hidden">
        {activeView === "resources" ? (
          <>
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-16">
              <div className="relative w-full sm:w-[313px] h-[37px]">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search"
                  value={resSearch}
                  onChange={(e) => setResSearch(e.target.value)}
                  className="w-full h-full border rounded-lg pl-10 pr-3 text-sm outline-none"
                />
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setActiveView("archived")}
                  className="text-[#4253C7] text-sm underline"
                >
                  Archived
                </button>
                <button
                  onClick={() => {
                    setInviteForm(DEFAULT_INVITE_FORM);
                    setInviteFormError("");
                    setIsDesignationOpen(false);
                    setIsSpecializationOpen(false);
                    setIsInviteOpen(true);
                  }}
                  className="bg-[#4253C7] text-white text-sm w-[163px] h-[40px] rounded-lg flex items-center justify-center gap-1"
                >
                  + Invite User
                </button>
              </div>
            </div>

            {/* Table */}
            <div
              className="mt-12 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
              style={{ minHeight: TABLE_MIN_HEIGHT }}
            >
              {" "}
              {isLoadingResources ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="animate-spin text-[#4253C7]" size={28} />
                </div>
              ) : resources.length === 0 ? (
                <p className="text-center text-gray-400 py-16">
                  No resources found.
                </p>
              ) : (
                <table className="w-full min-w-[500px] lg:min-w-0">
                  <thead>
                    <tr className="text-gray-400 text-[13px] border-b">
                      <th className="pb-3 font-normal text-left w-[16%]">
                        Resource
                      </th>
                      <th className="pb-3 font-normal text-center w-[12%]">
                        Role
                      </th>
                      <th className="pb-3 font-normal text-center w-[18%]">
                        Designation
                      </th>
                      <th className="pb-3 font-normal text-center w-[10%]">
                        Status
                      </th>
                      <th className="pb-3 font-normal text-center w-[15%]">
                        Disable/Enable
                      </th>
                      <th className="pb-3 font-normal text-right w-[5%]">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {resources.map((r) => (
                      <tr key={r._id} className="border-b text-[15px] h-[49px]">
                        {/* CHANGE 4 — Resource column */}
                        <td className="py-[14px] w-[28%]">
                          <div className="flex items-center gap-2">
                            {getProfileImageUrl(r.profilePicture) ? (
                              <img
                                src={getProfileImageUrl(r.profilePicture)}
                                alt={r.name}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-[13px] font-semibold text-gray-600">
                                {r.name?.[0] ?? "?"}
                              </div>
                            )}
                            <div>
                              <p className="font-normal text-[13px] text-black">
                                {r.name || "—"}
                              </p>
                              <p className="text-[11px] text-gray-400">
                                {r.email}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* CHANGE 5 — Role column */}
                        <td className="text-[13px] capitalize text-center">
                          {r.role}
                        </td>

                        {/* CHANGE 6 — Designation column */}
                        <td className="text-[13px] text-center">
                          {DESIGNATIONS.find(
                            (d) => d.value === r.designation,
                          )?.label.split(" - ")[0] ||
                            r.designation ||
                            "—"}
                        </td>
                        {/* CHANGE 7 — Status column */}
                        <td className="text-center">
                          <span
                            className={`text-[13px] px-3 py-1 rounded-full font-normal ${STATUS_STYLE[r.status] ?? "bg-gray-100 text-gray-500"}`}
                          >
                            {statusLabel(r.status)}
                          </span>
                        </td>

                        {/* CHANGE 8 — Disable/Enable column */}
                        <td className="text-center">
                          {r.status === "pending" ? (
                            <div className="flex items-center justify-center gap-2 w-full">
                              <div className="w-9 h-5 rounded-full bg-gray-200 relative opacity-70 flex-shrink-0">
                                <div className="w-4 h-4 bg-white rounded-full absolute top-0.5 left-0.5 shadow-sm" />
                              </div>
                              <span className="text-[13px] text-gray-300 whitespace-nowrap">
                                Locked
                              </span>
                            </div>
                          ) : (
                            <div className="flex justify-center">
                              {" "}
                              {/* ← new wrapper div added */}
                              <button
                                onClick={() => handleToggle(r)}
                                className={`w-9 h-5 rounded-full relative cursor-pointer transition-colors ${r.status === "active" ? "bg-[#4253C7]" : "bg-gray-300"}`}
                              >
                                <div
                                  className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-all shadow ${r.status === "active" ? "left-4" : "left-0.5"}`}
                                />
                              </button>
                            </div>
                          )}
                        </td>

                        {/* Action column — unchanged */}
                        <td className="text-center">
                          <div className="flex items-center justify-center gap-3">
                            <button
                              onClick={() => handleDelete(r)}
                              className="hover:opacity-70 transition-opacity"
                              aria-label={`Delete ${r.name || "user"}`}
                            >
                              <Trash2
                                size={18}
                                stroke="#FF5858"
                                strokeWidth={1.8}
                              />
                            </button>
                            <button
                              onClick={() => setPanelUserId(r._id)}
                              className="bg-[#E9EDF7] text-[13px] text-black opacity-60 px-3 py-1 rounded"
                            >
                              VIEW
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {resPagination.totalUsers > 0 && (
              <div className="bg-[#F5F5F5] border-t border-[#F1F1F1] h-[67px] px-6 -mx-5 -mb-5 mt-10 flex justify-between items-center gap-4 text-xs text-gray-500">
                {" "}
                <span>
                  Showing {resPagination.showing?.from} to{" "}
                  {resPagination.showing?.to} of {resPagination.showing?.of}{" "}
                  Results
                </span>
                <Pagination
                  current={resPage}
                  total={resPagination.totalPages}
                  onChange={handleResPage}
                />
              </div>
            )}
          </>
        ) : (
          /* ── Archived View ── */
          <>
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setActiveView("resources")}
                className="text-[#4253C7] text-sm underline"
              >
                Back
              </button>
            </div>

            <div className="relative w-full max-w-[313px] h-[37px] mb-10">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search"
                value={archSearch}
                onChange={(e) => setArchSearch(e.target.value)}
                className="w-full h-full border border-gray-200 rounded-lg pl-10 pr-3 text-sm outline-none shadow-sm"
              />
            </div>

            <div
              className="overflow-x-auto mt-8 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
              style={{ minHeight: TABLE_MIN_HEIGHT }}
            >
              {isLoadingArchived ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="animate-spin text-[#4253C7]" size={28} />
                </div>
              ) : archived.length === 0 ? (
                <p className="text-center text-gray-400 py-16">
                  No archived users.
                </p>
              ) : (
                <table className="w-full table-fixed min-w-[640px] lg:min-w-0">
                  <thead>
                    <tr className="text-left text-[12px] text-gray-400 border-b border-gray-100">
                      <th className="pb-3 font-normal w-[22%]">Resource</th>
                      <th className="pb-3 font-normal w-[12%]">Role</th>
                      <th className="pb-3 font-normal w-[22%]">Designation</th>
                      <th className="pb-3 font-normal w-[15%]">Archived On</th>
                      <th className="pb-3 font-normal w-[17%]">Archived By</th>
                      <th className="pb-3 font-normal text-center w-[13%]">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {archived.map((r) => (
                      <tr
                        key={r._id}
                        className="border-b border-gray-50 text-[15px]"
                      >
                        <td className="py-4">
                          <div className="flex items-center gap-3">
                            {getProfileImageUrl(r.profilePicture) ? (
                              <img
                                src={getProfileImageUrl(r.profilePicture)}
                                alt={r.name}
                                className="w-[30px] h-[30px] rounded-full object-cover border border-gray-300"
                              />
                            ) : (
                              <div className="w-[30px] h-[30px] rounded-full border border-gray-300 bg-white flex items-center justify-center flex-shrink-0">
                                <svg
                                  width="15"
                                  height="15"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#7F7F7F"
                                  strokeWidth="1.5"
                                >
                                  <circle cx="12" cy="8" r="3.5" />
                                  <path d="M5 20c0-3.5 3.1-6 7-6s7 2.5 7 6" />
                                </svg>
                              </div>
                            )}
                            <div>
                              <p className="text-[13px] font-normal text-black">
                                {r.name || "—"}
                              </p>{" "}
                              <p className="text-[10px] text-gray-400">
                                {r.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="text-[13px] text-black capitalize">
                          {r.role}
                        </td>
                        <td className="text-[13px] text-black">
                          {r.designation || "—"}
                        </td>
                        <td>
                          <p className="text-[13px] text-black">
                            {fmt(r.archivedAt)}
                          </p>
                          <p className="text-[10px] text-gray-400">
                            {fmtTime(r.archivedAt)}
                          </p>
                        </td>
                        <td className="text-[13px] text-black">
                          {r.disabledBy?.name || "—"}
                        </td>
                        <td className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleDelete(r)}
                              className="hover:opacity-70 transition-opacity"
                            >
                              <Trash2
                                size={18}
                                stroke="#FF5858"
                                strokeWidth={1.6}
                              />
                            </button>
                            <button
                              onClick={() => handleRestore(r._id)}
                              className="border border-[#F6F6F6] text-[#4253C7] text-[12px] px-[7px] py-1 rounded-[4px] flex items-center gap-1"
                            >
                              <RefreshCw size={10} />
                              RESTORE
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {archPagination.totalUsers > 0 && (
              <div className="flex justify-between items-center bg-[#F5F5F5] border-t border-[#F1F1F1] h-[67px] px-6 -mx-5 -mb-5 mt-20 text-xs text-gray-500">
                <span className="text-xs text-gray-400">
                  Showing {archPagination.showing?.from} to{" "}
                  {archPagination.showing?.to} of {archPagination.showing?.of}{" "}
                  Results
                </span>
                <Pagination
                  current={archPage}
                  total={archPagination.totalPages}
                  onChange={handleArchPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Permission Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-6">
        {[
          {
            icon: <Icons.Admin />,
            title: "Admin Access",
            desc: "Full Access To Manage Resources, Project Settings, Permissions, Reports And Organization-Level Controls.",
          },
          {
            icon: <Icons.Manager />,
            title: "Manager Access",
            desc: "Can Manage Teams, Assign Tasks, Approve Requests, And Monitor Resource Activities And Reports.",
          },
          {
            icon: <Icons.Developer />,
            title: "Developer Access",
            desc: "Can Access Assigned Projects, Update Task Progress, Manage Timesheets, And Collaborate With Teams.",
          },
          {
            icon: <Icons.Viewer />,
            title: "Viewer Access",
            desc: "Read-Only Access To Dashboards, Reports And Project Information Without Edit Permissions.",
          },
        ].map(({ icon, title, desc }) => (
          <div key={title} className="bg-white rounded-xl p-5">
            <div className="flex items-center gap-2 text-[#4C5CE7] font-semibold">
              {icon}
              {title}
            </div>
            <p className="text-xs text-gray-500 mt-3">{desc}</p>
          </div>
        ))}
      </div>

      {/* ── Invite Modal ── */}
      {isInviteOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-40 z-50"
            onClick={() => setIsInviteOpen(false)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl z-50 w-[668px] p-8 shadow-2xl">
            <div className="flex items-start justify-between mb-1">
              <div>
                <h2 className="text-2xl font-semibold">Invite User</h2>
                <p className="text-sm text-gray-400 mt-1">
                  Invite team members and assign access roles
                </p>
              </div>
              <button
                onClick={() => setIsInviteOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                ✕
              </button>
            </div>
            <hr className="my-4" />

            {inviteFormError && (
              <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-2 rounded-lg mb-4">
                {inviteFormError}
              </div>
            )}

            <div className="mb-5">
              <label className="block text-sm mb-2">Email Address</label>
              <input
                type="email"
                name="email"
                value={inviteForm.email}
                onChange={handleInviteChange}
                placeholder="eg: johndoe@qwickbit.com"
                className="w-full border border-gray-200 rounded-lg px-4 h-[42px] text-sm outline-none focus:border-[#4253C7]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              <div>
                <label className="block text-sm mb-2">Role</label>
                <div className="relative">
                  <select
                    name="role"
                    value={inviteForm.role}
                    onChange={handleInviteChange}
                    className="w-full border border-gray-300 rounded-lg px-4 h-[42px] text-sm outline-none appearance-none bg-white pr-10 cursor-pointer"
                  >
                    <option value="member">Member</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  <svg
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-600"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </div>
              <div>
                <label className="block text-sm mb-2">Specialization</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      if (isViewer) return;
                      setIsSpecializationOpen(!isSpecializationOpen);
                      setIsDesignationOpen(false);
                    }}
                    disabled={isViewer}
                    className={`w-full border border-gray-300 rounded-lg px-4 h-[42px] text-sm flex items-center justify-between ${isViewer ? "opacity-40 cursor-not-allowed bg-gray-100" : "bg-white"}`}
                  >
                    <span
                      className={`truncate ${selectedSpecializations.length ? "text-black" : "text-gray-400"}`}
                    >
                      {specializationLabel}
                    </span>
                    <svg
                      className="text-gray-600 flex-shrink-0"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  {isSpecializationOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsSpecializationOpen(false)}
                      />
                      <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-[200px] overflow-y-auto z-50">
                        {SPECIALIZATIONS.map((s) => (
                          <label
                            key={s}
                            className="px-4 py-2 text-sm hover:bg-[#4253C7] hover:text-white cursor-pointer flex items-center gap-3"
                          >
                            <input
                              type="checkbox"
                              checked={selectedSpecializations.includes(s)}
                              onChange={() => handleSpecializationToggle(s)}
                              className="w-4 h-4 accent-[#4253C7]"
                            />
                            <span>{s}</span>
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm mb-2">Start Date</label>

                <div className="relative">
                  {/* Icon */}
                  <div
                    className="absolute left-0 top-0 h-[42px] w-[42px] flex items-center justify-center rounded-l-[8px] bg-[#0404040A] border border-gray-300 border-r-0 cursor-pointer z-10"
                    onClick={() =>
                      document.getElementById("startDatePicker").focus()
                    }
                  >
                    <Icons.Calendar />
                  </div>
                  <DatePicker
                    id="startDatePicker"
                    wrapperClassName="w-full"
                    selected={
                      inviteForm.dateOfJoining
                        ? parseLocalDate(inviteForm.dateOfJoining)
                        : null
                    }
                    onChange={(date) =>
                      setInviteForm((prev) => ({
                        ...prev,
                        dateOfJoining: date ? formatLocalDate(date) : "",
                      }))
                    }
                    placeholderText="Select start date"
                    className="w-full h-[42px] border border-gray-300 rounded-lg pl-[50px] pr-3 text-sm font-normal text-black outline-none placeholder:text-gray-400"
                    style={{ fontSize: "14px" }}
                    dateFormat="dd MMM yyyy"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm mb-2">Designation</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      if (isViewer) return;
                      setIsDesignationOpen(!isDesignationOpen);
                      setIsSpecializationOpen(false);
                    }}
                    disabled={isViewer}
                    className={`w-full border border-gray-300 rounded-lg px-4 h-[42px] text-sm flex items-center justify-between ${isViewer ? "opacity-40 cursor-not-allowed bg-gray-100" : "bg-white"}`}
                  >
                    <span className={`truncate ${inviteForm.designation ? "text-black" : "text-gray-400"}`}>
                      {inviteForm.designation || "Select designation"}
                    </span>
                    <svg
                      className="text-gray-600 flex-shrink-0"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  {isDesignationOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsDesignationOpen(false)}
                      />
                      <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-[200px] overflow-y-auto z-50">
                        {DESIGNATIONS.map((d) => (
                          <div
                            key={d.value}
                            onClick={() => {
                              setInviteForm((p) => ({
                                ...p,
                                designation: d.value,
                              }));
                              setIsDesignationOpen(false);
                            }}
                            className="px-4 py-2 text-sm hover:bg-[#4253C7] hover:text-white cursor-pointer"
                          >
                            {d.label}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {isViewer && (
              <div className="mb-6">
                <label className="block text-sm mb-2">Description</label>
                <input
                  type="text"
                  name="description"
                  value={viewerDescription}
                  maxLength={100}
                  onChange={handleInviteChange}
                  placeholder="Enter a description"
                  className="w-full h-[42px] border border-gray-300 rounded-lg px-4 text-sm outline-none focus:border-[#4253C7]"
                />
                <p className="mt-1 text-right text-xs text-gray-400">
                  {viewerDescription.length}/100
                </p>
              </div>
            )}

            <div className="bg-[#F0F0FF] border border-gray-100 rounded-lg px-5 py-4 flex items-center gap-3 mb-6">
              <span className="text-[#4253C7] text-lg">ℹ</span>
              <p className="text-xs text-[#4253C7]">
                An Invitation Will Be Sent To This Email To Complete Their
                Account Setup.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsInviteOpen(false)}
                className="border border-[#4253C7] text-[#4253C7] w-[163px] h-[40px] rounded-lg text-sm flex items-center justify-center"
              >
                Cancel
              </button>
              <button
                onClick={handleInviteSubmit}
                disabled={isInviting}
                className="bg-[#4253C7] text-white w-[163px] h-[40px] rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {isInviting && <Loader2 size={14} className="animate-spin" />}{" "}
                Send Invite link
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Invite Sent Modal ── */}
      {isInviteSentOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-40 z-50"
            onClick={() => setIsInviteSentOpen(false)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl z-50 w-[560px] p-8 shadow-2xl">
            <div className="flex items-start justify-between mb-1">
              <h2 className="text-2xl font-semibold">Invite Sent</h2>
              <button
                onClick={() => setIsInviteSentOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                ✕
              </button>
            </div>
            <hr className="my-4" />
            <div className="flex flex-col items-center text-center mb-5">
              <div className="w-20 h-20 rounded-full bg-[#F0F0FF] flex items-center justify-center mb-3">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#4253C7"
                  strokeWidth="2"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold">Invite Sent!</h3>
            </div>
            <p className="text-xs text-gray-500 text-center mb-5 leading-relaxed">
              An email has been sent to{" "}
              <strong className="text-black">{inviteForm.email}</strong> with a
              secure setup link. The resource will appear in the table as{" "}
              <span className="text-amber-500 font-medium">"PENDING"</span>{" "}
              until they complete onboarding.
            </p>
            <div className="bg-[#F1F5F9] border border-gray-100 rounded-lg p-5 mb-6">
              <p className="text-sm font-medium mb-3">What Happens Next</p>
              {[
                "Resource receives email with setup link",
                "They click the link → complete their profile details",
                "They set up the password",
                "They have onboarded",
              ].map((step, i) => (
                <p key={i} className="text-xs text-gray-500 mb-1">
                  {i + 1}. {step}
                </p>
              ))}
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsInviteSentOpen(false)}
                className="border border-[#4253C7] text-[#4253C7] w-[163px] h-[40px] rounded-lg text-sm flex items-center justify-center"
              >
                Cancel
              </button>
              <button
                onClick={() => setIsInviteSentOpen(false)}
                className="bg-[#4253C7] text-white w-[163px] h-[40px] rounded-lg text-sm flex items-center justify-center"
              >
                Done
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Side Panel ── */}
      {panelUserId && (
        <SidePanel userId={panelUserId} onClose={() => setPanelUserId(null)} />
      )}
      <ConfirmToggleModal
        action={confirmAction}
        onCancel={() => setConfirmAction(null)}
        onConfirm={handleConfirmToggle}
        isLoading={isToggling}
        error={toggleError}
      />
      <DeleteUserModal
        user={deleteUser}
        onCancel={() => {
          setDeleteUser(null);
          setDeleteError("");
        }}
        onConfirm={handleConfirmDelete}
        isLoading={isDeletingUser}
        error={deleteError}
        permanent={isPermanentDelete}
      />
    </>
  );
};

export default Resources;
